<template>
    <div class="rma-details">
        <v-dialog
            v-model="isOpen"
            width="1040"
            :fullscreen="$vuetify.breakpoint.smAndDown"
            content-class="rma-details-dialog__overlay rma-details-dialog__overlay--main"
        >
            <v-card class="rma-details-dialog__card">
                <header class="rma-details-dialog__intro">
                    <span class="rma-details-dialog__intro-mark" aria-hidden="true">
                        <v-icon>{{ mdiWrenchOutline }}</v-icon>
                    </span>
                    <div class="rma-details-dialog__intro-copy">
                        <span class="rma-details-dialog__eyebrow">SERVICE REQUEST / ACCOUNT HISTORY</span>
                        <h2>RMA #{{ rmaValues.uid || 'Details' }}</h2>
                        <p>Review the repair progress, device information, and return details.</p>
                    </div>
                    <v-btn icon dark aria-label="Close RMA details" @click="isOpen = false">
                        <v-icon>{{ mdiClose }}</v-icon>
                    </v-btn>
                </header>

                <section v-if="rmaValues.uid" class="rma-details-dialog__device" aria-label="Device summary">
                    <div class="rma-details-dialog__device-copy">
                        <span class="rma-details-dialog__eyebrow">SERVICE REQUEST FOR</span>
                        <strong>{{ rmaValues.unit && rmaValues.unit.model || 'Getac device' }}</strong>
                        <span>SERIAL / {{ rmaValues.unit && rmaValues.unit.serial || 'Unavailable' }}</span>
                    </div>
                    <div class="rma-details-dialog__device-badges">
                        <span class="rma-details-dialog__status">
                            <span aria-hidden="true"></span>
                            {{ (formatStatus(rmaValues.customerStatus)) || 'Status unavailable' }}
                        </span>
                        <span v-if="isAdvanceExchange" class="rma-details-dialog__type">ADVANCE EXCHANGE</span>
                    </div>
                </section>

                <div class="rma-details-dialog__scroll">
                    <template v-if="rmaValues.uid">
                        <div class="rma-details-dialog__toolbar">
                            <span>RMA #{{ rmaValues.uid }}<template v-if="isAdvanceExchange"> · Advance Exchange</template></span>
                            <div class="rma-details-dialog__quick-actions">
                                <v-btn
                                    type="button"
                                    class="rma-details-dialog__action rma-details-dialog__action--secondary"
                                    v-if="rmaValues.flags && rmaValues.flags.allowFedexGroundPickup && rmaValues.flags.allowCancel"
                                    outlined
                                    @click="scheduleGroundReturnPickup"
                                >
                                    FedEx Ground Pickup
                                </v-btn>
                                <v-btn
                                    type="button"
                                    class="rma-details-dialog__action rma-details-dialog__action--secondary"
                                    v-if="rmaValues.flags && rmaValues.flags.allowRepairSummary"
                                    outlined
                                    :loading="summaryLoading"
                                    @click="repairSummary"
                                >
                                    Repair Summary
                                </v-btn>
                                <v-btn
                                    type="button"
                                    class="rma-details-dialog__action rma-details-dialog__action--primary"
                                    depressed
                                    @click="openSupport"
                                >
                                    Need help?
                                </v-btn>
                            </div>
                        </div>

                        <p
                            v-if="rmaValues.flags && rmaValues.flags.allowFedexGroundPickup && rmaValues.flags.allowCancel"
                            class="rma-details-dialog__note"
                        >
                            FedEx may need several hours to recognize a new Ground Return tracking number before pickup can be scheduled.
                        </p>

                        <v-alert v-if="summaryError" type="error" outlined dense>
                            Error generating the repair summary report.
                        </v-alert>
                        <v-alert v-if="cancelError" type="error" outlined dense>
                            Error canceling this RMA. Please try again.
                        </v-alert>

                        <section class="rma-details-dialog__sections" aria-label="RMA information">
                            <article
                                v-for="section in visibleSections"
                                :key="`${section.eyebrow}-${section.title}`"
                                class="rma-details-dialog__section"
                            >
                                <header class="rma-details-dialog__section-heading">
                                    <div>
                                        <span class="rma-details-dialog__eyebrow">{{ section.eyebrow }}</span>
                                        <h3>{{ section.title }}</h3>
                                    </div>
                                </header>
                                <dl class="rma-details-dialog__facts">
                                    <div v-for="fact in visibleFacts(section.facts)" :key="fact.path" class="rma-details-dialog__fact">
                                        <dt>{{ fact.title }}</dt>
                                        <dd>
                                            <a
                                                v-if="fact.path === 'tracking.inbound.labelUrl'"
                                                :href="valueFor(fact)"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >Download PDF</a>
                                            <a
                                                v-else-if="fact.path === 'tracking.inbound.trackingNumber' && trackingUrl('inbound')"
                                                :href="trackingUrl('inbound')"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >{{ valueFor(fact) }}</a>
                                            <a
                                                v-else-if="fact.path === 'tracking.outbound.trackingNumber' && trackingUrl('outbound')"
                                                :href="trackingUrl('outbound')"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >{{ valueFor(fact) }}</a>
                                            <template v-else-if="fact.path === 'stage.preQuote.quoteId'">
                                                {{ valueFor(fact) }}
                                                <service-details-quote :quote-id="String(valueFor(fact))" />
                                            </template>
                                            <template v-else>{{ valueFor(fact) }}</template>
                                        </dd>
                                    </div>
                                </dl>
                            </article>
                        </section>
                    </template>
                    <div v-else class="rma-details-dialog__empty">
                        <span class="rma-details-dialog__eyebrow">SERVICE DETAILS</span>
                        <h3>Details are not available yet</h3>
                        <p>No RMA details have been provided from our repair center. Contact support if you think this is an error.</p>
                        <v-btn class="rma-details-dialog__action rma-details-dialog__action--primary" depressed @click="openSupport">
                            Send a Message
                        </v-btn>
                    </div>
                </div>

                <footer v-if="rmaValues.uid" class="rma-details-dialog__footer">
                    <v-btn
                        class="rma-details-dialog__action rma-details-dialog__action--danger"
                        v-if="rmaValues.flags && rmaValues.flags.allowCancel"
                        data-cy="btn-cancel-rma"
                        outlined
                        :loading="cancellingRma"
                        @click="cancelRma"
                    >
                        Cancel RMA
                    </v-btn>
                    <v-spacer />
                    <v-btn class="rma-details-dialog__action rma-details-dialog__action--primary" depressed @click="isOpen = false">
                        Close Details
                    </v-btn>
                </footer>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="sendMessage"
            width="600"
            :fullscreen="$vuetify.breakpoint.smAndDown"
            content-class="rma-details-dialog__overlay rma-details-dialog__overlay--support"
        >
            <v-card class="rma-details-dialog__card rma-details-dialog__support-card">
                <header class="rma-details-dialog__intro mb-5">
                    <span class="rma-details-dialog__intro-mark" aria-hidden="true">
                        <v-icon>{{ mdiWrenchOutline }}</v-icon>
                    </span>
                    <div class="rma-details-dialog__intro-copy">
                        <span class="rma-details-dialog__eyebrow">GETAC SUPPORT</span>
                        <h2>Create a case</h2>
                        <p>Send our support team a message about this service request.</p>
                    </div>
                    <v-btn dark icon aria-label="Close support form" @click="sendMessage = false">
                        <v-icon>{{ mdiClose }}</v-icon>
                    </v-btn>
                </header>
                <v-card-text class="rma-details-dialog__support-content">
                    <sf-form
                        :key="`${rmaValues.uid || ''}-${caseKeyState}`"
                        outlined
                        dense
                        :serial-number="rmaValues.unit && rmaValues.unit.serial"
                        :model-name="rmaValues.unit && rmaValues.unit.model"
                        align-button="right"
                        :rma-number="rmaValues.uid"
                        private-form
                        @change="caseKeyState++"
                        @complete="sendMessage = false"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>

        <component
            :is="legacyComponent"
            v-show="false"
            ref="legacyDetails"
            :rma-values="rmaValues"
            aria-hidden="true"
            tabindex="-1"
        />
    </div>
</template>

<script>
import Vue from 'vue'
import get from 'lodash/get'
import { mdiClose, mdiWrenchOutline } from '@mdi/js'
import { formatDateShort } from '@/utils'
import { userCancelRma } from '@/api'
import sfForm from '@/components/support/sf_form'
import ServiceDetails from '@/views/user_account/rma_history/service_details.vue'
import ServiceDetailsQuote from '@/views/user_account/rma_history/quote_details.vue'

/** @typedef {{ title: string, path: string }} ReviewFact */
/** @typedef {{ eyebrow: string, title: string, facts: ReviewFact[] }} ReviewSection */

export default Vue.extend({
    name: 'DashboardRmaDetailsDialog',
    components: { ServiceDetails, ServiceDetailsQuote, sfForm },
    props: {
        rmaValues: {
            type: Object,
            default() {
                return {}
            }
        }
    },
    data() {
        return {
            mdiClose,
            mdiWrenchOutline,
            isOpen: false,
            sendMessage: false,
            cancellingRma: false,
            cancelError: false,
            summaryError: false,
            summaryLoading: false,
            caseKeyState: 0
        }
    },
    computed: {
        legacyComponent() {
            return ServiceDetails
        },
        isAdvanceExchange() {
            return this.rmaValues.rmaType === 'GVS_ADVANCE_EXCHANGE'
        },
        /** @returns {ReviewSection[]} */
        sections() {
            if (!this.rmaValues.uid) return []
            const tracking = this.rmaValues.tracking || {}
            const sections = [
                {
                    eyebrow: 'SERVICE REQUEST',
                    title: 'Request status',
                    facts: [
                        { title: 'Latest Status', path: 'customerStatus' },
                        { title: 'RMA Created Date', path: 'createdAt' },
                        ...(this.rmaValues.closedAt
                            ? [{ title: 'RMA Closed Date', path: 'closedAt' }]
                            : [])
                    ]
                }
            ]
            if (this.isAdvanceExchange && this.rmaValues.stage?.shipAdvanceExchange) {
                sections.push({
                    eyebrow: 'SERVICE REQUEST',
                    title: 'Replacement device',
                    facts: [
                        { title: 'Serial Number', path: 'stage.shipAdvanceExchange.parts.0.partSerial' },
                        { title: 'Part Number', path: 'stage.shipAdvanceExchange.parts.0.partNumber' }
                    ]
                })
            }
            if (tracking.inbound || tracking.outbound) {
                const facts = []
                if (tracking.inbound?.labelUrl) facts.push({ title: 'Shipping Label', path: 'tracking.inbound.labelUrl' })
                if (tracking.inbound) facts.push({ title: 'Inbound Tracking #', path: 'tracking.inbound.trackingNumber' })
                if (tracking.outbound) facts.push({ title: 'Outbound Tracking #', path: 'tracking.outbound.trackingNumber' })
                sections.push({ eyebrow: 'RETURN PLAN', title: 'Tracking & Shipping', facts })
            }
            const quoteId = get(this.rmaValues, 'stage.preQuote.quoteId')
            if (quoteId) {
                sections.push({
                    eyebrow: 'SERVICE REQUEST',
                    title: 'Paid Quote Details',
                    facts: [{ title: 'Quote ID', path: 'stage.preQuote.quoteId' }]
                })
            }
            sections.push(
                {
                    eyebrow: 'DEVICE & ISSUE',
                    title: 'Reported RMA User Data',
                    facts: [
                        { title: 'Primary Fault', path: 'primaryFault' },
                        { title: 'Problem', path: 'problem' },
                        { title: 'User Notes', path: 'initialNotes' },
                        { title: 'User including Hard Case', path: 'acc.hardCase' },
                        { title: 'User including AC Power', path: 'acc.acPower' },
                        { title: 'User including battery', path: 'acc.battery' },
                        { title: 'User including storage (HDD/SSD)', path: 'acc.storage' },
                        { title: 'Windows System Re-Image Approval', path: 'permissions.reimage' },
                        { title: 'BIOS Upgrade Approval', path: 'permissions.biosFirmware' },
                        { title: 'Windows User Name', path: 'unit.windowsUserName' },
                        { title: 'Unit Ref ID', path: 'custReference' },
                        { title: 'Asset Tag', path: 'assetTag' }
                    ]
                },
                {
                    eyebrow: 'DEVICE & ISSUE',
                    title: 'Unit Details',
                    facts: [
                        { title: 'Serial #', path: 'unit.serial' },
                        { title: 'Model', path: 'unit.model' },
                        { title: 'Warranty Expiration Date', path: 'unit.endDeviceWarranty' },
                        { title: 'Warranty Type', path: 'unit.warrantyType' }
                    ]
                },
                {
                    eyebrow: 'RETURN CONTACT',
                    title: 'Billing Information',
                    facts: [
                        { title: 'First Name', path: 'customer.billing.contact.firstName' },
                        { title: 'Email', path: 'customer.billing.contact.email' },
                        { title: 'Phone', path: 'customer.billing.contact.phone' },
                        { title: 'Organization', path: 'customer.billing.contact.company' },
                        { title: 'Position', path: 'customer.billing.contact.position' },
                        { title: 'Address 1', path: 'customer.billing.address.line1' },
                        { title: 'Address 2', path: 'customer.billing.address.line2' },
                        { title: 'City', path: 'customer.billing.address.city' },
                        { title: 'State', path: 'customer.billing.address.state' },
                        { title: 'Country', path: 'customer.billing.address.country' },
                        { title: 'Zip/Postal Code', path: 'customer.billing.address.postalCode' }
                    ]
                },
                {
                    eyebrow: 'RETURN CONTACT',
                    title: 'Shipping Information',
                    facts: [
                        { title: 'First Name', path: 'customer.shipping.contact.firstName' },
                        { title: 'Last Name', path: 'customer.shipping.contact.lastName' },
                        { title: 'Email', path: 'customer.shipping.contact.email' },
                        { title: 'Phone', path: 'customer.shipping.contact.phone' },
                        { title: 'Organization', path: 'customer.shipping.contact.company' },
                        { title: 'Position', path: 'customer.shipping.contact.position' },
                        { title: 'Address 1', path: 'customer.shipping.address.line1' },
                        { title: 'Address 2', path: 'customer.shipping.address.line2' },
                        { title: 'City', path: 'customer.shipping.address.city' },
                        { title: 'State', path: 'customer.shipping.address.state' },
                        { title: 'Country', path: 'customer.shipping.address.country' },
                        { title: 'Zip/Postal Code', path: 'customer.shipping.address.postalCode' }
                    ]
                }
            )
            return sections
        },
        /** @returns {ReviewSection[]} */
        visibleSections() {
            return this.sections.filter((section) => this.visibleFacts(section.facts).length)
        }
    },
    methods: {
        formatStatus(str) {
            if (!str) return ''
            return str.charAt(0).toUpperCase() + str.slice(1)
        },
        open() {
            this.isOpen = true
        },
        /** @param {ReviewFact} fact */
        valueFor(fact) {
            let value = get(this.rmaValues, fact.path, null)
            if (value === null || typeof value === 'undefined' || value === '') return '—'
            if (fact.path === 'problem') {
                value = [value, ...(this.rmaValues.additionalProblems || [])].join(', ')
            }
            if (['createdAt', 'closedAt', 'unit.endDeviceWarranty'].includes(fact.path)) {
                return formatDateShort(new Date(value))
            }
            if (typeof value === 'boolean') return String(value)
            if (Array.isArray(value)) return value.join(', ')
            return value
        },
        /** @param {ReviewFact[]} facts */
        visibleFacts(facts) {
            return facts.filter((fact) => {
                const value = get(this.rmaValues, fact.path, null)
                return value !== null && typeof value !== 'undefined' && value !== ''
            })
        },
        trackingUrl(direction) {
            return get(this.rmaValues, `tracking.${direction}.trackingUrl`, '')
        },
        scheduleGroundReturnPickup() {
            this.$refs.legacyDetails.scheduleGroundReturnPickup()
        },
        async repairSummary() {
            this.summaryLoading = true
            this.summaryError = false
            await this.$refs.legacyDetails.repairSummary()
            this.summaryError = this.$refs.legacyDetails.summaryError
            this.summaryLoading = false
            if (this.summaryError) {
                setTimeout(() => {
                    this.summaryError = false
                }, 5000)
            }
        },
        async cancelRma() {
            if (!confirm(`Are you sure you wish to cancel RMA# ${this.rmaValues.uid}?`)) return
            this.cancellingRma = true
            this.cancelError = false
            try {
                if (!this.rmaValues.closedAt && this.rmaValues.uid) {
                    await userCancelRma({ rmaNumber: this.rmaValues.uid })
                    this.$emit('reloadHistory')
                }
                this.isOpen = false
            } catch (error) {
                this.cancelError = true
                setTimeout(() => {
                    this.cancelError = false
                }, 5000)
            } finally {
                this.cancellingRma = false
            }
        },
        openSupport() {
            this.isOpen = false
            this.sendMessage = true
        }
    }
})
</script>

<style>
.rma-details-dialog__overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-y: hidden !important;
}
.rma-details-dialog__overlay--main.v-dialog {
    height: calc(100vh - 32px);
    max-height: calc(100vh - 32px) !important;
    margin: 16px;
    overflow: hidden !important;
}
.rma-details-dialog__overlay--support.v-dialog {
    height: auto;
    max-height: calc(100vh - 32px) !important;
    margin: 16px;
    overflow: hidden !important;
}
.rma-details-dialog__card {
    display: flex;
    width: 100%;
    height: 100%;
    max-height: 100%;
    flex-direction: column;
    overflow: hidden;
    color: #f1f2f5 !important;
    background: #282a32 !important;
    border: 1px solid rgba(255, 255, 255, 0.12) !important;
    border-radius: 10px !important;
    box-shadow: 0 22px 65px rgba(0, 0, 0, 0.42) !important;
}
.rma-details-dialog__intro {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 15px;
    padding: 21px 25px 18px;
    background: linear-gradient(115deg, rgba(219, 81, 32, 0.16), rgba(219, 81, 32, 0.035) 58%, transparent);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.rma-details-dialog__intro-mark {
    display: grid;
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    color: #fff;
    background: #db5120;
    border-radius: 10px;
    place-items: center;
}
.rma-details-dialog__intro-mark .v-icon {
    color: inherit;
    font-size: 22px;
}
.rma-details-dialog__intro-copy {
    min-width: 0;
    flex: 1;
}
.rma-details-dialog__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #f2a58b;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.2px;
}
.rma-details-dialog__intro h2 {
    margin: 0;
    color: #fff;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    font-size: 23px;
    font-weight: 600;
    letter-spacing: -0.45px;
}
.rma-details-dialog__intro p {
    margin: 4px 0 0;
    color: #c6c8d0;
    font-size: 12px;
    line-height: 1.5;
}
.rma-details-dialog__intro > .v-btn {
    color: #c6c8d0;
}
.rma-details-dialog__device {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 18px;
    margin: 17px 24px 0;
    padding: 14px 17px;
    background: linear-gradient(105deg, rgba(219, 81, 32, 0.17), rgba(219, 81, 32, 0.045) 65%, transparent);
    border-left: 4px solid #f0784c;
}
.rma-details-dialog__device-copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 3px;
}
.rma-details-dialog__device-copy strong {
    color: #fff;
    font-size: 16px;
    font-weight: 600;
}
.rma-details-dialog__device-copy > span:last-child {
    color: #d4d5dc;
    font-size: 11px;
    letter-spacing: 0.45px;
    overflow-wrap: anywhere;
}
.rma-details-dialog__device .rma-details-dialog__eyebrow {
    margin-bottom: 0;
}
.rma-details-dialog__status,
.rma-details-dialog__type {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 7px;
    padding: 7px 10px;
    color: #ffd5c4;
    background: rgba(219, 81, 32, 0.12);
    border: 1px solid rgba(240, 120, 76, 0.3);
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
}
.rma-details-dialog__device-badges {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}
.rma-details-dialog__status > span {
    width: 6px;
    height: 6px;
    background: #f0784c;
    border-radius: 50%;
}
.rma-details-dialog__type {
    color: #d4d5dc;
    background: rgba(255, 255, 255, 0.035);
    border-color: rgba(255, 255, 255, 0.14);
    letter-spacing: 0.5px;
}
.rma-details-dialog__scroll {
    min-height: 0;
    flex: 1 1 auto;
    overflow: auto;
    margin-top: 13px;
    padding: 0 24px 18px;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.rma-details-dialog__scroll::-webkit-scrollbar {
    width: 9px;
}
.rma-details-dialog__scroll::-webkit-scrollbar-track {
    background: #20222a;
    border-radius: 9px;
}
.rma-details-dialog__scroll::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.rma-details-dialog__scroll::-webkit-scrollbar-thumb:hover {
    background: #ff9a74;
}
.rma-details-dialog__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 2px 0 12px;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
}
.rma-details-dialog__quick-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 7px;
}
.rma-details-dialog__action.v-btn {
    min-width: 0;
    min-height: 38px;
    padding: 0 14px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.15px;
    text-transform: none;
    transition: background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease;
}
.rma-details-dialog__action.v-btn::before {
    opacity: 0 !important;
}
.rma-details-dialog__action.v-btn:hover {
    transform: translateY(-1px);
}
.rma-details-dialog__action.v-btn:focus-visible {
    outline: 2px solid #ff9a74;
    outline-offset: 3px;
}
.rma-details-dialog__action--secondary.v-btn {
    color: #ffd1bf !important;
    background: rgba(219, 81, 32, 0.075);
    border: 1px solid rgba(240, 120, 76, 0.38);
}
.rma-details-dialog__action--secondary.v-btn:hover {
    background: rgba(219, 81, 32, 0.17);
    border-color: rgba(240, 120, 76, 0.72);
    box-shadow: 0 5px 14px rgba(219, 81, 32, 0.12);
}
.rma-details-dialog__action--primary.v-btn {
    color: #fff !important;
    background: linear-gradient(135deg, #ed7042, #cb481e) !important;
    border: 1px solid rgba(255, 177, 145, 0.38);
    box-shadow: 0 5px 14px rgba(197, 72, 32, 0.2);
}
.rma-details-dialog__action--primary.v-btn:hover {
    background: linear-gradient(135deg, #fb8052, #db5120) !important;
    box-shadow: 0 7px 18px rgba(197, 72, 32, 0.3);
}
.rma-details-dialog__action--danger.v-btn {
    color: #ffb2a8 !important;
    background: rgba(188, 54, 50, 0.1);
    border: 1px solid rgba(242, 112, 105, 0.38);
}
.rma-details-dialog__action--danger.v-btn:hover {
    background: rgba(188, 54, 50, 0.2);
    border-color: rgba(242, 112, 105, 0.7);
    box-shadow: 0 5px 14px rgba(188, 54, 50, 0.12);
}
.rma-details-dialog__action.v-btn.v-btn--loading {
    pointer-events: none;
}
.rma-details-dialog__action.v-btn.v-btn--disabled {
    opacity: 0.55;
}
.rma-details-dialog__note {
    margin: 0 0 10px;
    color: #c7c9d0;
    font-size: 11px;
    line-height: 1.5;
}
.rma-details-dialog__scroll > .v-alert {
    margin: 10px 0;
}
.rma-details-dialog__sections {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 25px;
}
.rma-details-dialog__section {
    min-width: 0;
    padding: 15px 0 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
}
.rma-details-dialog__section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 9px;
}
.rma-details-dialog__section-heading h3 {
    margin: 0;
    color: #fff;
    font-family: 'Work Sans', 'Roboto', sans-serif;
    font-size: 15px;
    font-weight: 600;
}
.rma-details-dialog__facts {
    display: flex;
    flex-direction: column;
    margin: 0;
}
.rma-details-dialog__fact {
    display: grid;
    grid-template-columns: minmax(100px, 0.8fr) minmax(0, 1.2fr);
    gap: 12px;
    padding: 7px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
.rma-details-dialog__fact:last-child {
    border-bottom: 0;
}
.rma-details-dialog__fact dt,
.rma-details-dialog__fact dd {
    min-width: 0;
    margin: 0;
    font-size: 11px;
    line-height: 1.5;
    overflow-wrap: anywhere;
}
.rma-details-dialog__fact dt {
    color: #bfc1ca;
}
.rma-details-dialog__fact dd {
    color: #f1f2f5;
    font-weight: 500;
    text-align: right;
    white-space: pre-wrap;
}
.rma-details-dialog__fact dd ::v-deep a {
    color: #ff9a74;
    font-weight: 600;
    text-decoration: none;
    text-underline-offset: 2px;
}
.rma-details-dialog__fact dd ::v-deep a:hover {
    text-decoration: underline;
}
.rma-details-dialog__fact dd ::v-deep .v-card {
    margin-top: 6px;
    color: #f1f2f5;
    background: #34363e;
}
.rma-details-dialog__footer {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 12px;
    min-height: 66px;
    padding: 12px 24px max(15px, env(safe-area-inset-bottom));
    background: #282a32;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.rma-details-dialog__empty {
    padding: 22px 0;
}
.rma-details-dialog__empty h3 {
    margin: 0;
    color: #fff;
    font-size: 16px;
}
.rma-details-dialog__empty p {
    margin: 7px 0 15px;
    color: #c6c8d0;
    font-size: 12px;
    line-height: 1.6;
}
.rma-details-dialog__support-card {
    height: auto;
    max-height: calc(100vh - 32px);
}
.rma-details-dialog__support-content {
    overflow-y: auto;
    padding: 18px 24px 22px;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.rma-details-dialog__support-content::-webkit-scrollbar {
    width: 9px;
}
.rma-details-dialog__support-content::-webkit-scrollbar-track {
    background: #20222a;
    border-radius: 9px;
}
.rma-details-dialog__support-content::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
@media (max-width: 700px) {
    .rma-details-dialog__overlay {
        align-items: stretch;
    }
    .rma-details-dialog__overlay--main.v-dialog,
    .rma-details-dialog__overlay--support.v-dialog {
        width: 100% !important;
        height: 100vh;
        max-height: 100vh !important;
        margin: 0;
    }
    @supports (height: 100dvh) {
        .rma-details-dialog__overlay--main.v-dialog,
        .rma-details-dialog__overlay--support.v-dialog {
            height: 100dvh;
            max-height: 100dvh !important;
        }
    }
    .rma-details-dialog__card,
    .rma-details-dialog__support-card {
        height: 100vh;
        max-height: 100vh;
        border: 0 !important;
        border-radius: 0 !important;
    }
    .rma-details-dialog__intro {
        gap: 11px;
        padding: 16px 16px 14px;
    }
    .rma-details-dialog__intro-mark {
        flex-basis: 38px;
        width: 38px;
        height: 38px;
    }
    .rma-details-dialog__intro h2 {
        font-size: 20px;
    }
    .rma-details-dialog__device {
        flex-wrap: wrap;
        gap: 10px;
        margin: 13px 15px 0;
        padding: 12px;
    }
    .rma-details-dialog__status,
    .rma-details-dialog__type {
        margin-left: 0;
    }
    .rma-details-dialog__device-badges {
        flex-wrap: wrap;
        margin-left: 0;
    }
    .rma-details-dialog__scroll {
        padding-right: 15px;
        padding-left: 15px;
    }
    .rma-details-dialog__toolbar {
        align-items: flex-start;
        flex-direction: column;
    }
    .rma-details-dialog__quick-actions {
        justify-content: flex-start;
    }
    .rma-details-dialog__action.v-btn {
        min-height: 40px;
    }
    .rma-details-dialog__sections {
        grid-template-columns: minmax(0, 1fr);
    }
    .rma-details-dialog__footer {
        min-height: 66px;
        padding-right: 15px;
        padding-left: 15px;
    }
}
@media (max-width: 420px) {
    .rma-details-dialog__fact {
        grid-template-columns: minmax(90px, 0.8fr) minmax(0, 1.2fr);
        gap: 8px;
    }
}
</style>
